/**
 * WebOs IPC Handler
 *
 * Gère le routage des messages entre les processus et le Kernel.
 * Traite les requêtes système (fs, process, etc.).
 */

import { createLogger } from '../utils/debug.js';
import { getFileSystem } from './filesystem.js';
import { getProcessManager, ProcessType } from './process-manager.js';
import { getNotificationService } from './notification-service.js';
import { getSystemWindowManager } from './system-window-manager.js';
import { getWMRegistry } from './wm-registry.js';

const log = createLogger('IPC');

/**
 * Gestionnaire IPC
 */
export class IPCHandler {
  constructor() {
    /** @type {Map<string, Function>} */
    this.handlers = new Map();

    /** @type {ProcessManager} */
    this.processManager = null;

    /** @type {FileSystem} */
    this.fs = null;

    /** @type {Function|null} */
    this.onUIEvent = null; // Pour les événements à destination du Shell

    /** @type {Function|null} */
    this.onExecCommand = null; // Pour exécuter des commandes shell

    this.registerDefaultHandlers();
    log.debug('IPCHandler initialized');
  }

  /**
   * Configure les dépendances
   * @param {ProcessManager} processManager
   * @param {FileSystem} fs
   */
  configure(processManager, fs) {
    this.processManager = processManager;
    this.fs = fs;

    // Configurer le handler de messages du ProcessManager
    processManager.onProcessMessage = (pid, message) => {
      this.handleMessage(pid, message);
    };

    log.debug('IPCHandler configured with ProcessManager and FileSystem');
  }

  /**
   * Enregistre un handler pour un type de message
   * @param {string} type
   * @param {Function} handler
   */
  registerHandler(type, handler) {
    this.handlers.set(type, handler);
    log.debug('Handler registered:', type);
  }

  /**
   * Enregistre les handlers par défaut
   */
  registerDefaultHandlers() {
    // Système de fichiers
    this.registerHandler('REQ_FS_READ', this.handleFsRead.bind(this));
    this.registerHandler('REQ_FS_WRITE', this.handleFsWrite.bind(this));
    this.registerHandler('REQ_FS_STAT', this.handleFsStat.bind(this));
    this.registerHandler('REQ_FS_READDIR', this.handleFsReaddir.bind(this));
    this.registerHandler('REQ_FS_MKDIR', this.handleFsMkdir.bind(this));
    this.registerHandler('REQ_FS_DELETE', this.handleFsDelete.bind(this));
    this.registerHandler('REQ_FS_EXISTS', this.handleFsExists.bind(this));
    this.registerHandler('REQ_FS_SYMLINK', this.handleFsSymlink.bind(this));
    this.registerHandler('REQ_FS_READLINK', this.handleFsReadlink.bind(this));
    this.registerHandler('REQ_FS_RENAME', this.handleFsRename.bind(this));
    this.registerHandler('REQ_FS_COPY', this.handleFsCopy.bind(this));

    // Processus
    this.registerHandler('REQ_SPAWN', this.handleSpawn.bind(this));
    this.registerHandler('REQ_KILL', this.handleKill.bind(this));
    this.registerHandler('REQ_WAIT', this.handleWait.bind(this));
    this.registerHandler('REQ_PROCESS_LIST', this.handleProcessList.bind(this));

    // Divers
    this.registerHandler('REQ_ENV_GET', this.handleEnvGet.bind(this));

    // Notifications
    this.registerHandler('REQ_NOTIFY_SEND', this.handleNotifySend.bind(this));
    this.registerHandler('REQ_NOTIFY_DISMISS', this.handleNotifyDismiss.bind(this));
    this.registerHandler('REQ_NOTIFY_CLEAR', this.handleNotifyClear.bind(this));
    this.registerHandler('REQ_NOTIFY_STATUS', this.handleNotifyStatus.bind(this));
    this.registerHandler('REQ_NOTIFY_START', this.handleNotifyStart.bind(this));
    this.registerHandler('REQ_NOTIFY_STOP', this.handleNotifyStop.bind(this));

    // Window Manager Registry
    this.registerHandler('REQ_WM_REGISTER', this.handleWMRegister.bind(this));
    this.registerHandler('REQ_WM_UNREGISTER', this.handleWMUnregister.bind(this));
    this.registerHandler('REQ_WM_GET_IFRAME', this.handleWMGetIframe.bind(this));
    this.registerHandler('REQ_WM_INFO', this.handleWMInfo.bind(this));

    // Autostart
    this.registerHandler('REQ_AUTOSTART_GET', this.handleAutostartGet.bind(this));
    this.registerHandler('REQ_AUTOSTART_SET', this.handleAutostartSet.bind(this));

    // Applications list
    this.registerHandler('REQ_APPS_LIST', this.handleAppsList.bind(this));

    // Exécution de commandes shell
    this.registerHandler('REQ_EXEC', this.handleExec.bind(this));
  }

  /**
   * Traite un message entrant
   * @param {number} fromPid
   * @param {Object} message
   */
  async handleMessage(fromPid, message) {
    const { type, id, payload } = message;

    log.debug(`Handling ${type} from PID ${fromPid}`, payload);

    const handler = this.handlers.get(type);

    if (!handler) {
      log.warn(`No handler for message type: ${type}`);
      this.sendError(fromPid, id, 'EINVAL', `Unknown message type: ${type}`);
      return;
    }

    try {
      const result = await handler(fromPid, payload, message);
      // Extraire les transferables si le handler en retourne
      const transferables = result?.transferable || [];
      // Ne pas inclure le champ transferable dans la réponse sérialisée
      if (result?.transferable) {
        delete result.transferable;
      }
      this.sendSuccess(fromPid, id, result, transferables);
    } catch (error) {
      log.error(`Handler error for ${type}:`, error.message);
      this.sendError(fromPid, id, error.code || 'EINTERNAL', error.message);
    }
  }

  /**
   * Envoie une réponse de succès
   * @param {number} toPid
   * @param {string} requestId
   * @param {any} data
   * @param {Transferable[]} transferables
   */
  sendSuccess(toPid, requestId, data, transferables = []) {
    const response = {
      type: 'RES_SUCCESS',
      id: requestId,
      from: 0,
      to: toPid,
      timestamp: Date.now(),
      payload: { data }
    };

    this.processManager.sendToProcess(toPid, response, transferables);
  }

  /**
   * Envoie une réponse d'erreur
   * @param {number} toPid
   * @param {string} requestId
   * @param {string} code
   * @param {string} message
   */
  sendError(toPid, requestId, code, message) {
    const response = {
      type: 'RES_ERROR',
      id: requestId,
      from: 0,
      to: toPid,
      timestamp: Date.now(),
      payload: { code, message }
    };

    this.processManager.sendToProcess(toPid, response);
  }

  /**
   * Envoie un événement UI au Shell
   * @param {string} eventType
   * @param {Object} payload
   */
  emitUIEvent(eventType, payload) {
    if (this.onUIEvent) {
      this.onUIEvent(eventType, payload);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS SYSTÈME DE FICHIERS
  // ═══════════════════════════════════════════════════════════════

  async handleFsRead(pid, payload) {
    const { path, encoding } = payload;
    log.debug(`FS_READ: ${path} (PID ${pid})`);

    const content = await this.fs.readFile(path, encoding || 'utf8');

    // Si binaire, transférer comme ArrayBuffer
    if (encoding === 'binary' && content instanceof ArrayBuffer) {
      return { content, transferable: true };
    }

    return { content };
  }

  async handleFsWrite(pid, payload) {
    const { path, data, options } = payload;
    log.debug(`FS_WRITE: ${path} (PID ${pid})`);

    await this.fs.writeFile(path, data, options);
    return { success: true };
  }

  async handleFsStat(pid, payload) {
    const { path } = payload;
    log.debug(`FS_STAT: ${path} (PID ${pid})`);

    const stat = await this.fs.stat(path);
    return stat;
  }

  async handleFsReaddir(pid, payload) {
    const { path } = payload;
    log.debug(`FS_READDIR: ${path} (PID ${pid})`);

    const entries = await this.fs.readdir(path);
    return { entries };
  }

  async handleFsMkdir(pid, payload) {
    const { path, options } = payload;
    log.debug(`FS_MKDIR: ${path} (PID ${pid})`);

    await this.fs.mkdir(path, options);
    return { success: true };
  }

  async handleFsDelete(pid, payload) {
    const { path, options } = payload;
    log.debug(`FS_DELETE: ${path} (PID ${pid})`);

    await this.fs.delete(path, options);
    return { success: true };
  }

  async handleFsExists(pid, payload) {
    const { path } = payload;
    log.debug(`FS_EXISTS: ${path} (PID ${pid})`);

    const exists = await this.fs.exists(path);
    return { exists };
  }

  async handleFsSymlink(pid, payload) {
    const { target, path } = payload;
    log.debug(`FS_SYMLINK: ${path} -> ${target} (PID ${pid})`);

    await this.fs.symlink(target, path);
    return { success: true };
  }

  async handleFsReadlink(pid, payload) {
    const { path } = payload;
    log.debug(`FS_READLINK: ${path} (PID ${pid})`);

    const target = await this.fs.readlink(path);
    return { target };
  }

  async handleFsRename(pid, payload) {
    const { oldPath, newPath } = payload;
    log.debug(`FS_RENAME: ${oldPath} -> ${newPath} (PID ${pid})`);

    await this.fs.rename(oldPath, newPath);
    return { success: true };
  }

  async handleFsCopy(pid, payload) {
    const { src, dest, options } = payload;
    log.debug(`FS_COPY: ${src} -> ${dest} (PID ${pid})`);

    await this.fs.copy(src, dest, options);
    return { success: true };
  }

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS PROCESSUS
  // ═══════════════════════════════════════════════════════════════

  async handleSpawn(pid, payload) {
    const { bin, args, env, cwd } = payload;
    log.debug(`SPAWN: ${bin} (from PID ${pid})`);

    const parentProcess = this.processManager.getProcess(pid);
    const parentEnv = parentProcess?.env || {};

    const process = await this.processManager.spawn(bin, args || [], {
      env: { ...parentEnv, ...env },
      cwd: cwd || parentEnv.PWD,
      parentPid: pid
    });

    // Si c'est une app GUI, gérer la fenêtre
    if (process.type === ProcessType.GUI && process.iframe) {
      const wmRegistry = getWMRegistry();
      const manifest = process.manifest || {};
      const processPid = process.pid;

      // Mode fullscreen : l'app prend tout l'écran (ex: DeskWeb lancé via IPC)
      if (manifest.fullscreen === true) {
        this.setupFullscreenApp(process, manifest, processPid);
      } else if (wmRegistry.hasActiveWM()) {
        // Déléguer la création de fenêtre au WM externe
        log.debug(`Delegating window creation to external WM for PID ${processPid}`);

        // Donner un ID unique à l'iframe pour que le WM puisse la retrouver
        process.iframe.id = `webos-iframe-${processPid}`;

        // Envoyer la demande de création de fenêtre au WM
        const sent = wmRegistry.requestCreateWindow({
          pid: processPid,
          title: manifest.displayName || process.name,
          icon: manifest.icon || null,
          width: this.parseDimension(manifest.windowWidth, window.innerWidth) || 600,
          height: this.parseDimension(manifest.windowHeight, window.innerHeight) || 400,
          minWidth: manifest.minWidth || 200,
          minHeight: manifest.minHeight || 150
        });

        if (!sent) {
          log.warn(`Failed to send window request to WM, falling back to SystemWindowManager`);
          this.createSystemWindow(process, manifest, processPid);
        }
      } else {
        // Pas de WM externe, utiliser SystemWindowManager
        this.createSystemWindow(process, manifest, processPid);
      }
    }

    return {
      pid: process.pid,
      name: process.name
    };
  }

  /**
   * Parse une dimension qui peut être un nombre ou un pourcentage
   * @param {number|string} value - La valeur (ex: 600, "100%", "50%")
   * @param {number} reference - La référence pour les pourcentages
   * @returns {number|null}
   */
  parseDimension(value, reference) {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      if (value.endsWith('%')) {
        const percent = parseFloat(value);
        if (!isNaN(percent)) {
          return Math.floor(reference * percent / 100);
        }
      }
      const num = parseInt(value, 10);
      if (!isNaN(num)) return num;
    }
    return null;
  }

  /**
   * Configure une app en mode plein écran (sans fenêtre)
   * @param {Object} process - Le processus
   * @param {Object} manifest - Le manifest de l'app
   * @param {number} processPid - Le PID du processus
   */
  setupFullscreenApp(process, manifest, processPid) {
    log.debug(`Setting up fullscreen app: ${manifest.displayName || process.name} (PID ${processPid})`);

    const iframe = process.iframe;

    // Positionner l'iframe en plein écran
    // Note: z-index 100 permet aux fenêtres d'apps (z-index 101+) d'apparaître au-dessus
    iframe.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      border: none;
      z-index: 100;
      display: block;
      background: #1e1e2e;
    `;

    // Quand le processus se termine, retirer l'iframe
    this.processManager.waitForProcess(processPid).then((exitCode) => {
      log.debug(`Fullscreen app finished: PID ${processPid} (code: ${exitCode})`);
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    });
  }

  /**
   * Crée une fenêtre via le SystemWindowManager (fallback)
   * @param {Object} process - Le processus
   * @param {Object} manifest - Le manifest de l'app
   * @param {number} processPid - Le PID du processus
   */
  createSystemWindow(process, manifest, processPid) {
    const windowManager = getSystemWindowManager();

    const win = windowManager.createWindow({
      title: manifest.displayName || process.name,
      pid: processPid,
      iframe: process.iframe,
      width: this.parseDimension(manifest.windowWidth, window.innerWidth) || 600,
      height: this.parseDimension(manifest.windowHeight, window.innerHeight) || 400,
      minWidth: manifest.minWidth || 200,
      minHeight: manifest.minHeight || 150,
      // Callback quand la fenêtre est fermée : terminer le processus
      onClose: () => {
        if (this.processManager.getProcess(processPid)) {
          this.processManager.terminateProcess(processPid, 0, false);
        }
      }
    });

    log.debug(`System window created for process: ${win.id} (PID ${processPid})`);

    // Quand le processus se termine, fermer la fenêtre
    this.processManager.waitForProcess(processPid).then((exitCode) => {
      const existingWin = windowManager.getWindowByPid(processPid);
      if (existingWin) {
        windowManager.closeWindow(existingWin.id);
      }
    });
  }

  async handleKill(pid, payload) {
    const { targetPid, signal } = payload;
    log.debug(`KILL: PID ${targetPid} signal ${signal} (from PID ${pid})`);

    const force = signal === 'SIGKILL';
    this.processManager.terminateProcess(targetPid, 0, force);

    return { success: true };
  }

  async handleWait(pid, payload) {
    const { targetPid } = payload;
    log.debug(`WAIT: for PID ${targetPid} (from PID ${pid})`);

    const exitCode = await this.processManager.waitForProcess(targetPid);
    return { exitCode, pid: targetPid };
  }

  async handleProcessList(pid, payload) {
    log.debug(`PROCESS_LIST (from PID ${pid})`);

    const processes = this.processManager.listActiveProcesses().map(p => ({
      pid: p.pid,
      name: p.name,
      type: p.type,
      state: p.state,
      parentPid: p.parentPid,
      startTime: p.startTime
    }));

    return { processes };
  }

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS DIVERS
  // ═══════════════════════════════════════════════════════════════

  async handleEnvGet(pid, payload) {
    const { name } = payload;
    const process = this.processManager.getProcess(pid);

    if (!process) {
      throw new Error('Process not found');
    }

    if (name) {
      return { value: process.env[name] };
    }

    return { env: { ...process.env } };
  }

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS NOTIFICATIONS
  // ═══════════════════════════════════════════════════════════════

  async handleNotifySend(pid, payload) {
    const { title, message, type = 'info', duration = 5000 } = payload;
    log.debug(`NOTIFY_SEND: [${type}] "${title}" (from PID ${pid})`);

    const service = getNotificationService();
    const notificationId = service.send(title, message, type, duration);

    return { notificationId };
  }

  async handleNotifyDismiss(pid, payload) {
    const { notificationId } = payload;
    log.debug(`NOTIFY_DISMISS: ${notificationId} (from PID ${pid})`);

    const service = getNotificationService();
    service.dismiss(notificationId);

    return { success: true };
  }

  async handleNotifyClear(pid, payload) {
    log.debug(`NOTIFY_CLEAR (from PID ${pid})`);

    const service = getNotificationService();
    service.clearAll();

    return { success: true };
  }

  async handleNotifyStatus(pid, payload) {
    log.debug(`NOTIFY_STATUS (from PID ${pid})`);

    const service = getNotificationService();
    return service.getStatus();
  }

  async handleNotifyStart(pid, payload) {
    log.debug(`NOTIFY_START (from PID ${pid})`);

    const service = getNotificationService();
    service.start();

    return { success: true };
  }

  async handleNotifyStop(pid, payload) {
    log.debug(`NOTIFY_STOP (from PID ${pid})`);

    const service = getNotificationService();
    service.stop();

    return { success: true };
  }

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS WINDOW MANAGER REGISTRY
  // ═══════════════════════════════════════════════════════════════

  async handleWMRegister(pid, payload, message) {
    const { capabilities = ['windows'] } = payload;
    log.debug(`WM_REGISTER: PID ${pid} with capabilities:`, capabilities);

    const wmRegistry = getWMRegistry();
    const process = this.processManager.getProcess(pid);

    if (!process) {
      throw new Error('Process not found');
    }

    // Créer un MessageChannel pour la communication WM ↔ Kernel
    const channel = new MessageChannel();

    // Stocker le port côté kernel dans le registry
    const result = wmRegistry.register(pid, channel.port1, capabilities);

    if (!result.success) {
      throw new Error(result.error);
    }

    // Configurer le port kernel pour recevoir les messages du WM
    channel.port1.onmessage = (event) => {
      this.handleWMMessage(pid, event.data);
    };
    channel.port1.start();

    // Retourner le port WM au processus via transferable
    return {
      success: true,
      port: channel.port2,
      transferable: [channel.port2]
    };
  }

  /**
   * Gère les messages venant du WM externe
   */
  handleWMMessage(wmPid, message) {
    const { type, data, requestId } = message;
    log.debug(`WM message from PID ${wmPid}:`, type);

    const wmRegistry = getWMRegistry();

    switch (type) {
      case 'WM_WINDOW_CLOSED':
        // Le WM a fermé une fenêtre, terminer le processus associé
        if (data.pid) {
          wmRegistry.removeWindowBounds(data.pid);
          if (this.processManager.getProcess(data.pid)) {
            this.processManager.terminateProcess(data.pid, 0, false);
          }
        }
        break;

      case 'WM_WINDOW_FOCUSED':
        // Notification informative, pas d'action requise
        log.debug(`Window focused: PID ${data.pid}`);
        break;

      case 'WM_WINDOW_BOUNDS':
        // Le WM envoie les bounds d'une fenêtre pour positionner l'iframe
        // data: { pid, x, y, width, height, visible, zIndex }
        if (data.pid) {
          wmRegistry.updateWindowBounds(data.pid, {
            x: data.x || 0,
            y: data.y || 0,
            width: data.width || 600,
            height: data.height || 400,
            visible: data.visible !== false,
            zIndex: data.zIndex || 100
          });
        }
        break;

      default:
        log.warn(`Unknown WM message type: ${type}`);
    }
  }

  async handleWMUnregister(pid, payload) {
    log.debug(`WM_UNREGISTER: PID ${pid}`);

    const wmRegistry = getWMRegistry();
    const result = wmRegistry.unregister(pid);

    if (!result.success) {
      throw new Error(result.error);
    }

    return { success: true };
  }

  async handleWMGetIframe(pid, payload) {
    const { targetPid } = payload;
    log.debug(`WM_GET_IFRAME: requesting iframe for PID ${targetPid} (from PID ${pid})`);

    // Vérifier que le demandeur est le WM actif
    const wmRegistry = getWMRegistry();
    const wmInfo = wmRegistry.getActiveWMInfo();

    if (!wmInfo || wmInfo.pid !== pid) {
      throw new Error('Only the active WM can request iframes');
    }

    const targetProcess = this.processManager.getProcess(targetPid);

    if (!targetProcess) {
      throw new Error(`Process ${targetPid} not found`);
    }

    if (!targetProcess.iframe) {
      throw new Error(`Process ${targetPid} has no iframe`);
    }

    // On ne peut pas transférer l'iframe elle-même via postMessage.
    // À la place, on retourne les informations de positionnement.
    // Le WM devra positionner l'iframe en overlay comme le fait SystemWindowManager.
    return {
      success: true,
      pid: targetPid,
      iframeId: targetProcess.iframe.id || null,
      // L'iframe reste dans le DOM principal, le WM la positionne en CSS
      message: 'Iframe stays in main DOM. Use CSS positioning to overlay it on your window content area.'
    };
  }

  async handleWMInfo(pid, payload) {
    log.debug(`WM_INFO (from PID ${pid})`);

    const wmRegistry = getWMRegistry();
    const info = wmRegistry.getActiveWMInfo();

    return {
      hasActiveWM: wmRegistry.hasActiveWM(),
      activeWM: info
    };
  }

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS AUTOSTART
  // ═══════════════════════════════════════════════════════════════

  async handleAutostartGet(pid, payload) {
    log.debug(`AUTOSTART_GET (from PID ${pid})`);

    const process = this.processManager.getProcess(pid);
    const home = process?.env?.HOME || '/home/user';
    const configPath = `${home}/.config/webos/autostart.json`;

    try {
      const content = await this.fs.readFile(configPath, 'utf8');
      return JSON.parse(content);
    } catch (e) {
      // Fichier n'existe pas, retourner config par défaut
      return {
        apps: [],
        deskwebAsked: false
      };
    }
  }

  async handleAutostartSet(pid, payload) {
    const { apps, deskwebAsked } = payload;
    log.debug(`AUTOSTART_SET: apps=${apps?.join(',')} (from PID ${pid})`);

    const process = this.processManager.getProcess(pid);
    const home = process?.env?.HOME || '/home/user';
    const configDir = `${home}/.config/webos`;
    const configPath = `${configDir}/autostart.json`;

    // Créer le dossier si nécessaire
    try {
      await this.fs.mkdir(configDir, { recursive: true });
    } catch (e) {
      // Dossier existe peut-être déjà
    }

    const config = {
      apps: apps || [],
      deskwebAsked: deskwebAsked ?? false
    };

    await this.fs.writeFile(configPath, JSON.stringify(config, null, 2));

    return { success: true };
  }

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS APPLICATIONS
  // ═══════════════════════════════════════════════════════════════

  async handleAppsList(pid, payload) {
    log.debug(`APPS_LIST (from PID ${pid})`);

    const apps = [];

    // Lire le registre des apps installées
    try {
      const installedPath = '/var/lib/webpkg/installed.json';
      const content = await this.fs.readFile(installedPath, 'utf8');
      const installed = JSON.parse(content);

      for (const [name, info] of Object.entries(installed)) {
        try {
          const manifestPath = `/usr/bin/${name}/manifest.json`;
          const manifestContent = await this.fs.readFile(manifestPath, 'utf8');
          const manifest = JSON.parse(manifestContent);

          apps.push({
            name,
            displayName: manifest.displayName || name,
            version: manifest.version || info.version,
            type: manifest.type || 'cli',
            icon: manifest.icon || null,
            description: manifest.description || null,
            permissions: manifest.permissions || []
          });
        } catch (e) {
          // Manifest non trouvé, ajouter avec infos minimales
          apps.push({
            name,
            displayName: name,
            version: info.version,
            type: 'cli',
            icon: null,
            description: null,
            permissions: []
          });
        }
      }
    } catch (e) {
      log.warn('Could not read installed apps registry:', e.message);
    }

    return { apps };
  }

  // ═══════════════════════════════════════════════════════════════
  // HANDLER EXÉCUTION DE COMMANDES
  // ═══════════════════════════════════════════════════════════════

  /**
   * Exécute une commande shell et retourne sa sortie
   * @param {number} pid - PID du processus demandeur
   * @param {Object} payload - { command, args, cwd, env }
   */
  async handleExec(pid, payload) {
    const { command, args = [], cwd, env } = payload;

    log.debug(`EXEC (from PID ${pid}): ${command} ${args.join(' ')}`);

    if (!this.onExecCommand) {
      throw new Error('Shell executor not configured');
    }

    try {
      const result = await this.onExecCommand(command, args, { cwd, env });
      return {
        output: result.output || '',
        exitCode: result.exitCode ?? 0,
        error: result.error || null
      };
    } catch (error) {
      return {
        output: '',
        exitCode: 1,
        error: error.message
      };
    }
  }
}

// Instance singleton
let instance = null;

/**
 * Obtient l'instance de l'IPCHandler
 * @returns {IPCHandler}
 */
export function getIPCHandler() {
  if (!instance) {
    instance = new IPCHandler();
  }
  return instance;
}
